import React, { useState, useEffect } from 'react';
import {
  FileText,
  Calendar,
  Users,
  DollarSign,
  TrendingUp,
  Building2,
  Package,
  BarChart3,
  Loader2,
  CheckCircle,
  AlertTriangle,
} from 'lucide-react';
import { getApiUrl, getAuthHeaders, getUser } from '../utils/auth';
import { generateCommissionPDF, generateSalesReportPDF, generateCommissionSummaryPDF } from '../utils/pdfGenerator';
import StyledDropdown from './StyledDropdown';
import PdfPreviewModal from './PdfPreviewModal';

const Reports = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [pdfDownloadName, setPdfDownloadName] = useState('report.pdf');
  const [pdfModalTitle, setPdfModalTitle] = useState('Report preview');

  // Filter state
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setPdfPreviewOpen(false);
  };

  const periodFileSlug = () => `${filterMonth}-${filterYear}`;

  const currentUser = getUser();
  const isAdmin = currentUser?.role === 'Superadmin' || currentUser?.role === 'Admin';
  const isGroupLeader = currentUser?.role === 'Group Leader';
  const isTeamLeader = currentUser?.role === 'Team Leader';

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate year options
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 2020 + 2 }, (_, i) => currentYear + 1 - i);

  const getPeriodString = () => {
    const monthName = months.find(m => m.value === filterMonth)?.label || '';
    return `${monthName} ${filterYear}`;
  };

  // Clear messages after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Generate Personal Commission Report
  const generatePersonalCommissionReport = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('month', filterMonth);
      params.append('year', filterYear);

      const response = await fetch(
        `${getApiUrl()}/api/commissions/my-commissions?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const result = await response.json();

      if (result.success) {
        const userInfo = {
          name: currentUser?.name || 'N/A',
          accountId: currentUser?.accountId || 'N/A',
          code: currentUser?.accountId || 'N/A',
          role: currentUser?.role || 'N/A',
          group: result.data?.user?.group || currentUser?.group || 'N/A',
          reportsTo: result.data?.user?.reportsTo || 'N/A',
        };
        
        const url = await generateCommissionPDF(result.data, userInfo, getPeriodString());
        setPdfDownloadName(`my-commission-${periodFileSlug()}.pdf`);
        setPdfModalTitle('Commission report preview');
        setPdfPreviewUrl(url);
        setPdfPreviewOpen(true);
        setSuccess('Commission report ready — preview opened.');
      } else {
        setError(result.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Report generation error:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate Sales Report (Admin only)
  const generateSalesReport = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.append('month', filterMonth);
      params.append('year', filterYear);

      const response = await fetch(
        `${getApiUrl()}/api/sales?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const result = await response.json();

      if (result.success) {
        const salesData = result.data.map(sale => ({
          date: sale.date,
          user: sale.user?.name || 'N/A',
          product: sale.product?.name || 'N/A',
          amount: sale.amount,
          group: sale.user?.group?.name || 'Unassigned',
        }));

        const url = await generateSalesReportPDF(salesData, getPeriodString(), 'Sales Summary Report', {
          groupByGroup: true,
        });
        setPdfDownloadName(`sales-report-${periodFileSlug()}.pdf`);
        setPdfModalTitle('Sales report preview');
        setPdfPreviewUrl(url);
        setPdfPreviewOpen(true);
        setSuccess('Sales report ready — preview opened.');
      } else {
        setError(result.message || 'Failed to generate report');
      }
    } catch (err) {
      console.error('Report generation error:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Generate Commission Summary Report (same layout as Commission Management export)
  const generateCommissionSummaryReport = async () => {
    setLoading(true);
    setError('');
    try {
      const period = getPeriodString();
      const slug = periodFileSlug();

      const params = new URLSearchParams();
      params.append('month', filterMonth);
      params.append('year', filterYear);

      const [groupsRes, usersRes, commissionsRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/groups`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/users`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/commissions/all?${params.toString()}`, { headers: getAuthHeaders() }),
      ]);

      const groupsResult = await groupsRes.json();
      const usersResult = await usersRes.json();
      const commissionsResult = await commissionsRes.json();

      if (!commissionsResult.success) throw new Error(commissionsResult.message || 'Failed to fetch commissions');
      if (!usersResult.success) throw new Error(usersResult.message || 'Failed to fetch users');

      // Include all non-admin users so the PDF matches the Commission Management export style.
      const relevantUsers = (usersResult.data || []).filter(
        (u) => u.role !== 'Admin' && u.role !== 'Superadmin'
      );

      const currentMonthData = commissionsResult.data.filter((c) => !c.isRetroactive);
      const retroData = commissionsResult.data.filter((c) => c.isRetroactive);

      // Aggregate by userId (same approach as CommissionManagement "Export PDF" when filterUser is empty)
      const commissionByUserId = {};

      const ensureUser = (uId, commUser) => {
        if (commissionByUserId[uId]) return;
        commissionByUserId[uId] = {
          personal: 0,
          teamOverride: 0,
          groupOverride: 0,
          retroactive: 0,
          total: 0,
          // Keep a reference in case comm records include group info, etc.
          user: commUser,
        };
      };

      currentMonthData.forEach((comm) => {
        const uId = comm.userId;
        ensureUser(uId, comm.user);

        const eligibleAmount = parseFloat(comm.eligibleAmount || 0);

        if (comm.commissionType === 'Personal') {
          commissionByUserId[uId].personal += eligibleAmount;
          commissionByUserId[uId].total += eligibleAmount;
        } else if (comm.commissionType === 'TL Override') {
          commissionByUserId[uId].teamOverride += eligibleAmount;
          commissionByUserId[uId].total += eligibleAmount;
        } else if (comm.commissionType === 'GL Override') {
          commissionByUserId[uId].groupOverride += eligibleAmount;
          commissionByUserId[uId].total += eligibleAmount;
        }
      });

      retroData.forEach((comm) => {
        const uId = comm.userId;
        ensureUser(uId, comm.user);

        const retroAmount = parseFloat(comm.commissionAmount || 0);
        commissionByUserId[uId].retroactive += retroAmount;
        commissionByUserId[uId].total += retroAmount;
      });

      const summaryData = relevantUsers.map((user) => {
        const qc = commissionByUserId[user.id] || {
          personal: 0,
          teamOverride: 0,
          groupOverride: 0,
          retroactive: 0,
          total: 0,
        };

        return {
          name: user.name || 'N/A',
          code: user.accountId || 'N/A',
          role: user.role || 'N/A',
          group: user.group?.name || 'Unassigned',
          personal: qc.personal,
          teamOverride: qc.teamOverride,
          groupOverride: qc.groupOverride,
          retroactive: qc.retroactive,
          total: qc.total,
        };
      });

      const totalRetroactive = summaryData.reduce((sum, u) => sum + (parseFloat(u.retroactive) || 0), 0);

      // groupName = null => "COMMISSION SUMMARY REPORT" title, but the generator will still group rows by group.
      const url = await generateCommissionSummaryPDF(summaryData, period, null, totalRetroactive);

      setPdfDownloadName(`commission-summary-${slug}.pdf`);
      setPdfModalTitle('Commission summary preview');
      setPdfPreviewUrl(url);
      setPdfPreviewOpen(true);
      setSuccess('Commission summary ready — preview opened.');
    } catch (err) {
      console.error('Report generation error:', err);
      setError('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Report definitions
  const personalReports = [
    {
      id: 'personal-commission',
      title: 'My Commission Statement',
      description: 'Detailed breakdown of your commission earnings including personal, TL override, and GL override commissions.',
      icon: DollarSign,
      color: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
      action: generatePersonalCommissionReport,
    },
  ];

  const leaderReports = [
    {
      id: 'team-performance',
      title: 'Team Performance Report',
      description: 'Sales and commission summary for your team members.',
      icon: Users,
      color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      action: generatePersonalCommissionReport, // Will be enhanced later
      comingSoon: true,
    },
  ];

  const adminReports = [
    {
      id: 'sales-report',
      title: 'Sales Report',
      description: 'Sales summary for the selected period, grouped by team with subtotals (same style as the commission summary report).',
      icon: TrendingUp,
      color: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      action: generateSalesReport,
    },
    {
      id: 'commission-payout',
      title: 'Commission Summary',
      description: 'Commission summary grouped by team/group (same format as Commission Management export).',
      icon: DollarSign,
      color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      action: generateCommissionSummaryReport,
    },
    {
      id: 'group-performance',
      title: 'Group Performance Report',
      description: 'Comparison of sales performance across all groups.',
      icon: Building2,
      color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
      action: generateSalesReport, // Will be enhanced later
      comingSoon: true,
    },
    {
      id: 'product-performance',
      title: 'Product Performance Report',
      description: 'Sales breakdown by product for the selected period.',
      icon: Package,
      color: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400',
      action: generateSalesReport, // Will be enhanced later
      comingSoon: true,
    },
  ];

  const ReportCard = ({ report }) => {
    const Icon = report.icon;
    
    return (
      <div className="neo-raised p-6 transition-colors duration-200">
        <div className="flex items-start gap-4">
          <div className={`p-3 rounded-lg ${report.color}`}>
            <Icon size={24} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text">
                {report.title}
              </h3>
              {report.comingSoon && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
                  Coming Soon
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 dark:text-dark-muted mt-1">
              {report.description}
            </p>
            <button
              onClick={report.action}
              disabled={loading || report.comingSoon}
              className={`mt-4 flex items-center gap-2 px-4 py-2 text-sm font-medium ${
                report.comingSoon
                  ? 'neo-btn text-gray-400 dark:text-dark-muted cursor-not-allowed'
                  : 'neo-btn-primary disabled:opacity-50 disabled:cursor-not-allowed'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText size={16} />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Reports</h1>
        <p className="text-gray-600 dark:text-dark-muted mt-1">
          Generate and download reports for analysis
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 transition-colors duration-200">
          <AlertTriangle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-green-700 dark:text-green-300 transition-colors duration-200">
          <CheckCircle size={20} />
          {success}
        </div>
      )}

      {/* Period Filter */}
      <div className="neo-raised p-4 mb-6 transition-colors duration-200">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-gray-400 dark:text-dark-muted" />
            <span className="text-sm font-medium text-gray-700 dark:text-dark-text">Report Period:</span>
          </div>
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
            value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            className="min-w-[150px]"
          />
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
            value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />
        </div>
      </div>

      {/* Personal Reports Section - Not for Admin (they don't have sales) */}
      {!isAdmin && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4 flex items-center gap-2">
            <DollarSign size={20} className="text-primary-600" />
            Personal Reports
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {personalReports.map(report => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        </div>
      )}

      {/* Leader Reports Section (for GL/TL) */}
      {(isGroupLeader || isTeamLeader) && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4 flex items-center gap-2">
            <Users size={20} className="text-blue-600" />
            Team Reports
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {leaderReports.map(report => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        </div>
      )}

      {/* Admin Reports Section */}
      {isAdmin && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4 flex items-center gap-2">
            <BarChart3 size={20} className="text-purple-600" />
            Administrative Reports
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {adminReports.map(report => (
              <ReportCard key={report.id} report={report} />
            ))}
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 transition-colors duration-200">
        <div className="flex items-start gap-3">
          <FileText size={20} className="text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-primary-800 dark:text-primary-300">About Reports</h3>
            <p className="text-sm text-primary-700 dark:text-primary-400 mt-1">
              Reports open in a preview window on this page. Use Save / Download to save the PDF — on phones this uses the system share sheet (e.g. Save to Files). All reports use the selected period above.
            </p>
          </div>
        </div>
      </div>

      <PdfPreviewModal
        open={pdfPreviewOpen}
        pdfUrl={pdfPreviewUrl}
        title={pdfModalTitle}
        downloadFilename={pdfDownloadName}
        onClose={closePdfPreview}
      />
    </div>
  );
};

export default Reports;

